import { Box, Button, Link, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { CatalogRow } from './CatalogRow';
import { ADDON_CATALOGS_SOFT_CAP } from '../../utils/subscriptionApi';
import type { Catalog } from '../../utils/subscriptionApi';
import type { BillingPeriod } from '../../utils/paymentTiers';

interface CatalogListProps {
  catalogs: Catalog[];
  onAdd: () => void;
  onRemove: (catalogAliId: string) => void;
  onSwitchCycle: (catalogAliId: string, target: BillingPeriod) => void;
  /** GH#1254: start a reactivation checkout for an EXPIRED catalog. */
  onReactivate?: (catalogAliId: string) => void;
}

export const CatalogList: React.FC<CatalogListProps> = ({
  catalogs,
  onAdd,
  onRemove,
  onSwitchCycle,
  onReactivate,
}) => {
  const addons = catalogs.filter((c) => !c.isPrimary);
  const atCap = addons.length >= ADDON_CATALOGS_SOFT_CAP;
  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1.5,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ m: 0 }}>Catalogs</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Each catalog represents a different creator. Up to {ADDON_CATALOGS_SOFT_CAP} additional per account.
          </Typography>
        </Box>
        {atCap ? (
          <Tooltip title={`You've reached the limit of ${ADDON_CATALOGS_SOFT_CAP} additional catalogs.`}>
            <Link href="mailto:support@artlegacyinstitute.org" underline="hover">Contact us</Link>
          </Tooltip>
        ) : (
          <Button variant="outlined" startIcon={<AddIcon />} onClick={onAdd}>
            Add Catalog
          </Button>
        )}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        {catalogs.map((c) => (
          <CatalogRow
            key={c.catalogAliId}
            catalog={c}
            onRemove={onRemove}
            onSwitchCycle={onSwitchCycle}
            onReactivate={onReactivate}
          />
        ))}
      </Box>
    </Box>
  );
};
